import { BillingUiStateKind } from './billing';
import { MessagingPlatform } from './messagingPlatform.enum';

export const ACCOUNT_DELETION_REASON = {
  TOO_EXPENSIVE: 'tooExpensive',
  MISSING_INTEGRATION: 'missingIntegration',
  AI_NOT_USEFUL: 'aiNotUseful',
  TOO_COMPLEX: 'tooComplex',
  SWITCHING_TOOL: 'switchingTool',
  NOT_USING_ENOUGH: 'notUsingEnough',
  PRIVACY: 'privacy',
  OTHER: 'other',
} as const;

export type AccountDeletionReason =
  (typeof ACCOUNT_DELETION_REASON)[keyof typeof ACCOUNT_DELETION_REASON];

export const ACCOUNT_DELETION_REASONS: ReadonlySet<string> = new Set(
  Object.values(ACCOUNT_DELETION_REASON),
);

export const ACCOUNT_DELETION_OTHER_MIN_LENGTH = 10;
export const ACCOUNT_DELETION_OTHER_MAX_LENGTH = 500;

export const ACCOUNT_DELETION_SOURCE = {
  USER_MODAL: 'userModal',
  CLERK_WEBHOOK: 'clerkWebhook',
  ADMIN: 'admin',
  /**
   * The teardown found no row to patch and minted one. By construction the
   * modal's write never landed, so stamping USER_MODAL here would make the
   * field indistinguishable from a real submission and the dashboard would
   * read "no reason given" as a user who declined to answer.
   */
  UNATTRIBUTED: 'unattributed',
} as const;

export type AccountDeletionSource =
  (typeof ACCOUNT_DELETION_SOURCE)[keyof typeof ACCOUNT_DELETION_SOURCE];

/**
 * One team the user belonged to, frozen at deletion time — the row dies with
 * them. No `team` prefix on the members: the type already says it.
 *
 * There is deliberately no `isOwner`: it is `role === TeamRole.OWNER`, so
 * storing it would be a derived duplicate that can disagree with its source.
 */
export interface AccountDeletionTeam {
  id: string;
  /**
   * Sealed. Hoisted into `teamNamesJson` at rest — the only user-authored text
   * on this object, and itself sealed on `HTTeams`, so it must be OPENED before
   * it reaches here or it seals twice and reads back as base64.
   */
  name?: string;
  role?: string;
  /**
   * Kept even though it is not the user's own attribute: churned-on-trial vs
   * churned-after-paying is the most valuable cross-tab the dashboard has, it
   * is derivable from nothing else, and the team row carrying it is destroyed
   * by the teardown.
   */
  subscriptionStatus?: string;
  /**
   * Resolved from the TEAM's billing evidence at the moment of deletion, and
   * frozen there — an owner who subscribes the day after a member leaves does
   * not change what that member's record says.
   */
  billingState?: BillingUiStateKind;
}

/**
 * DoBox entries the user authored. Measured BEFORE the teardown: deleting a
 * personal task group cascades through the TaskGroups stream to
 * `removeTaskGroupFromEntries`, which empties exactly the entries this counts.
 */
export interface AccountDeletionDoBoxTally {
  total: number;
  resolved: number;
  open: number;
  /** Keyed by `InboxEntrySource`: MANUAL, AGENT, RECURRENCE, INTEGRATION. */
  bySource: Record<string, number>;
  firstCreatedAt?: number;
  lastCreatedAt?: number;
}

/**
 * What a deletion counted, keyed by the thing it counted. The STORAGE contract,
 * not a log label — renaming a key changes what the dashboard reads, so it moves
 * with a migration rather than with a refactor.
 *
 * Providers sit in the same map as tables on purpose: `removeUserGoogleIntegrations`
 * reports one number for an account that spans several tables, and the dashboard
 * renders "what was removed" without caring which kind a key is.
 */
export const ACCOUNT_DELETION_COUNT = {
  CONTACTS: 'contacts',
  PEOPLE_AND_ACTIVITY: 'peopleAndActivity',
  DO_BOX_CONFIGS: 'doBoxConfigs',
  DOBOX_MONITORS: 'doBoxMonitors',
  NOTIFICATIONS: 'notifications',
  WEB_SOCKET_CONNECTIONS: 'webSocketConnections',
  SESSIONS: 'sessions',
  TASK_GROUPS: 'taskGroups',
  EMAIL_COUNTERS: 'emailCounters',
  COMMUNICATION_AGENTS: 'communicationAgents',
  UNREAD_ITEMS: 'unreadItems',
  KNOWLEDGE_SPACES: 'knowledgeSpaces',
  /**
   * What the userId sweep found AFTER every provider deleted its own platform —
   * residue, not the mailbox. Two things produce a non-zero: a credential row that
   * was already gone (so no REMOVE was left to cascade from), or a cascade that
   * had simply not caught up, since the sweep races the stream it runs behind.
   * Recorded, not alarmed — same reading as `s3ObjectsSweptAfterStream`. The
   * unambiguous loss signal is the UserCommunications stream's on-failure DLQ.
   *
   * Deliberately NOT the old `userCommunications` key, which counted the whole
   * mailbox: same name, two different measurements, and every historical row
   * carries the old one.
   */
  USER_COMMUNICATIONS_SWEPT: 'userCommunicationsSwept',
  SCAN_JOBS: 'scanJobs',
  MCP_CONNECTIONS: 'mcpConnections',
  META_CREDENTIALS: 'metaCredentials',
  LOCAL_DEVICES: 'localDevices',
  AGENT_CONVERSATIONS: 'agentConversations',
  AGENT_RUNS: 'agentRuns',
  AGENT_EXECUTION_HISTORY: 'agentExecutionHistory',
  CHAT_SESSIONS: 'chatSessions',
  ARTIFACTS: 'artifacts',
  GENERATED_IMAGES: 'generatedImages',
  USER_ATTACHMENTS: 'userAttachments',
  SCHEDULED_EMAILS: 'scheduledEmails',
  SNOOZED_EMAILS: 'snoozedEmails',
  EVAL_CONFIG: 'evalConfig',
  MEETING_POLLS: 'meetingPolls',
  TEAM_REQUESTS: 'teamRequests',
  AI_USAGE: 'aiUsage',
  TEAM_INVITES: 'teamInvites',
  /** Historical only — SlackWorkspaceCache is no longer enumerated by the teardown. */
  SLACK_WORKSPACE_CACHE: 'slackWorkspaceCache',
  OUTLOOK_FOLDER_CACHE: 'outlookFolderCache',
  INTEGRATION_REQUESTS: 'integrationRequests',
  PUSH_SUBSCRIPTIONS: 'pushSubscriptions',
  WORKFLOW_RECOMMENDATIONS: 'workflowRecommendations',
  AGENT_TEMPLATES: 'agentTemplates',
  KNOWLEDGE_FAVORITES: 'knowledgeFavorites',
  FLOW_GROUPS: 'flowGroups',
  FLOW_EXECUTION: 'flowExecution',
  FLOW_GENERATION_JOBS: 'flowGenerationJobs',
  RUNTIME_FLAGS: 'runtimeFlags',
  DOBOX_EXECUTOR_LEDGER: 'doboxExecutorLedger',
  DOBOX_DECISIONS: 'doboxDecisions',
  LOCAL_CODING_JOBS: 'localCodingJobs',
  AGENT_ROUTES: 'agentRoutes',
} as const;

export type AccountDeletionCountKey =
  | (typeof ACCOUNT_DELETION_COUNT)[keyof typeof ACCOUNT_DELETION_COUNT]
  | MessagingPlatform;

/** Absent means NOT MEASURED. Zero means measured and empty. */
export type AccountDeletionCounts = Partial<
  Record<AccountDeletionCountKey, number>
>;

/**
 * One row per account deletion, in AccountDeletions. It outlives every other
 * trace of the user, so it is the only record of why they left. `userId` is the
 * join key to the HTUserFunnel row DeleteUserData deliberately preserves.
 */
export interface AccountDeletion {
  userId: string;
  /**
   * Epoch ms. Sort key, and part of the AAD — so it never changes once written.
   */
  deletedAt: number;
  /** `YYYY-MM`, the AccountDeletionsByMonth partition. */
  deletionMonth: string;

  reasons: AccountDeletionReason[];
  otherReason?: string;

  email?: string;
  userName?: string;
  /** The user's job title, mirroring the sealed `HTUsers.Title`. */
  userTitle?: string;
  /**
   * The work areas picked during onboarding (`HTUsers.WorkAreas`). Sealed here,
   * hoisted into `workAreasJson` at rest — the envelope moves flat strings.
   * Readable on `HTUsers` itself, so it arrives in the clear.
   */
  userWorkAreas?: string[];

  teams?: AccountDeletionTeam[];
  deletionSource: AccountDeletionSource;
  /** Sealed: identifies a person, and is neither a key, a structural branch nor public. Set only for `deletionSource` admin. */
  deletedByUserId?: string;
  /** Sealed: free text an admin wrote about a third party. Set only for `deletionSource` admin. */
  adminNote?: string;

  /**
   * Everything the teardown removed, in one shape. Ten scalars used to live
   * here — one per table someone remembered to add — and a table added to the
   * registry now starts being counted with no schema change at all.
   */
  counts?: AccountDeletionCounts;
  /**
   * NOT a row count, which is why it is not in the map above: `removeMember`
   * transfers ownership rather than deleting when a successor exists, so this
   * counts teams left, not rows removed.
   */
  teamsLeftCount?: number;
  doBox?: AccountDeletionDoBoxTally;
  /** Epoch ms. Stamped by the most recent slice to land. */
  usageSnapshotAt?: number;
  /** Epoch ms, one per slice — the redelivery guards. See the slice constants. */
  integrationsSnapshotAt?: number;
  coreDataSnapshotAt?: number;
  doBoxSnapshotAt?: number;

  isTestAccount?: boolean;
  /** Epoch ms, set when the teardown finished. */
  purgedAt?: number;
  /**
   * DynamoDB TTL, in epoch **SECONDS** — not the epoch ms every other field
   * here uses. A millisecond value lands ~55,000 years out and the row would
   * never expire. Set once, at creation; two years of retention.
   */
  ttl?: number;
}

/**
 * Each slice stamps its own marker, and the marker is what the redelivery guard
 * tests. Guarding on a measured field instead would fail open exactly when it
 * matters: a group whose count came back undefined never writes that field, so
 * the guard stays absent and a retry over already-deleted data overwrites the
 * numbers the first attempt did measure.
 */
export const ACCOUNT_DELETION_SNAPSHOT_SLICE = {
  INTEGRATIONS: 'integrationsSnapshotAt',
  CORE_DATA: 'coreDataSnapshotAt',
  DO_BOX: 'doBoxSnapshotAt',
  MAILBOX_SWEEP: 'mailboxSweepSnapshotAt',
} as const;

export type AccountDeletionSnapshotSlice =
  (typeof ACCOUNT_DELETION_SNAPSHOT_SLICE)[keyof typeof ACCOUNT_DELETION_SNAPSHOT_SLICE];

/**
 * What group 1 and group 3 report back — derived from the record rather than
 * restated, so a count cannot be added to one and forgotten in the other. Every
 * field is optional: a group that failed contributes nothing rather than a zero,
 * so a partial teardown never records a count it did not observe.
 */
export type AccountDeletionUsageSnapshot = Partial<
  Pick<AccountDeletion, 'counts' | 'teamsLeftCount' | 'doBox'>
>;

/** The identity half of the record, which only the request path can supply. */
export type CreateAccountDeletionInput = Pick<
  AccountDeletion,
  | 'userId'
  | 'userName'
  | 'userTitle'
  | 'reasons'
  | 'otherReason'
  | 'userWorkAreas'
  | 'teams'
  | 'deletionSource'
  | 'deletedByUserId'
  | 'adminNote'
> & {
  /** Required here, optional on the record: the teardown can mint a row without one. */
  email: string;
};
